'use client';
import { cn } from '@/lib/utils';

import NextImage, { NextImageProps } from '@/components/atoms/Image';
import { Text } from '@/components/atoms/Text';
import Link from 'next/link';

import iconArrowSrc from '~/icons/arrow-up-right.svg';
import { SvgIcon } from '@/components/SvgIcon';

export type MenuPromoBannerProps = {
  image?: NextImageProps;
  title: string;
  url: string;
  description?: string;
  onLinkClick?: (
    params: { title: string } | { url: string; keepMenuOpen?: boolean }
  ) => void;
  layout?: 'horizontal' | 'vertical';
};

export function MenuPromoBanner({
  image,
  title,
  description,
  url,
  onLinkClick,
  layout = 'horizontal',
}: MenuPromoBannerProps) {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const currentPath = window.location.pathname;

    if (url === currentPath) {
      e.preventDefault();
      onLinkClick?.({ title, url });
      return;
    }

    onLinkClick?.({ title, url });
  };

  const isHorizontal = layout === 'horizontal';

  return (
    <Link
      href={url}
      onClick={handleClick}
      className={cn(
        'bg-brand-100 hover:bg-brand-100/70 flex w-full min-w-0 cursor-pointer items-start gap-x-3 rounded p-4 transition-colors duration-100'
      )}
    >
      <div className='flex min-w-0 flex-1 items-start gap-x-2'>
        {!!image && (
          <div className='shrink-0'>
            <NextImage {...image} height={24} width={24} aria-hidden />
          </div>
        )}
        <div
          className={cn(
            'flex min-w-0 flex-col gap-y-1',
            isHorizontal && 'headerLg:flex-row headerLg:items-center headerLg:gap-x-2 headerLg:gap-y-0'
          )}
        >
          <Text
            variant='h5'
            classNames={{
              wrapper: cn(
                'font-serif select-text text-base-dark-black text-wrap',
                isHorizontal && 'headerLg:truncate headerLg:text-nowrap'
              ),
            }}
          >
            {title}
          </Text>
          {!!description && (
            <Text
              variant='body-m'
              color='gray-dark-600'
              classNames={{
                wrapper: cn(
                  'relative min-w-0 max-w-full text-wrap',
                  isHorizontal && 'headerLg:min-w-[250px] headerLg:max-w-[400px]'
                ),
              }}
            >
              {description}
            </Text>
          )}
        </div>
      </div>
      <div className='relative ml-auto flex h-6 w-6 shrink-0 items-center justify-center'>
        <SvgIcon
          src={iconArrowSrc}
          className='headerLg:h-full headerLg:w-full h-5 w-5 text-gray-500'
        />
      </div>
    </Link>
  );
}
